import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Input, mergeClasses, Card, Badge, Button, Spinner, Tooltip } from '@fluentui/react-components';
import { SearchRegular, ChevronLeftRegular, ChevronRightRegular, ErrorCircleRegular, ArrowSortDownRegular } from '@fluentui/react-icons';
import { useManagerDashboardStyles } from './ManagerDashboard.styles';
import { CandidateDetailsPanel, CandidateDetails, OnboardingSection } from '../CandidateDetailsPanel';
import { RollupStatus, toRollupStatus } from '../../types';
import type { StatusSummary } from './ManagerDashboard.types';
import { usePrehiresData, useUserRoles, mapPrehiresToCandidates, searchPersons } from '../../services';
import { StatusInfoDto, AppRole, PrehireDto } from '../../generated/api.onboarding.client';
import { useNoAccess } from '../../context';
import { PersonRegular, PeopleTeamRegular, PersonChatRegular, GlobePersonRegular } from '@fluentui/react-icons';
import BannerEmoji from '../../assets/managerBanner.svg';
import NoPrehireData from '../../assets/NoPrehireData.svg';
import WobCheck from '../../assets/WOBCheck.svg';
import { detectLang } from '../../utils/detectLang';

const ITEMS_PER_PAGE = 15;

type SortField = 'name' | 'jobTitle' | 'startDate' | 'requisitionNumber' | 'hrCaseNumber' | 'location' | 'progress' | 'hiringManagerEmail';
type SortDirection = 'asc' | 'desc';

interface Candidate {
    id: number;
    name: string;
    jobTitle: string;
    startDate: string;
    startDateValue?: number;
    location: string;
    currentStage: string;
    email: string;
    caseNumber: string;
    requisitionNumber: string;
    hrCaseNumber: string;
    completedSteps: number;
    totalSteps: number;
    hasInProgressSteps: boolean;
    daysToStart?: number;
    needsAttention?: boolean;
    hiringManagerEmail?: string;
    statuses?: { [key: string]: StatusInfoDto };
}

interface WorkOnBehalfManager {
    email: string;
    displayName: string;
    prehires: PrehireDto[];
}

const getProgressBarClass = (
    completed: number,
    total: number,
    needsAttention?: boolean,
): string => {
    if (needsAttention) return 'needsAttention';
    if (completed >= total) return 'complete';
    if (completed > 0) return 'inProgress';
    return 'notStarted';
};

const STATUS_DISPLAY_NAMES: Record<string, string> = {
    backgroundcheck: 'Background check',
    immigration: 'Immigration',
    i9: 'I-9 documents',
    i9documents: 'I-9 documents',
    contract: 'Employment agreement',
    employmentagreement: 'Employment agreement',
    ecstatus: 'Employee profile created',
    employeeprofile: 'Employee profile created',
    relocation: 'Relocation',
    startdate: 'Start date',
    microsoftaccount: 'Microsoft account setup',
    offeraccepted: 'Offer accepted',
};

const resolveStatus = (info: StatusInfoDto | undefined): RollupStatus => {
    if (!info?.rollupStatus) return RollupStatus.NotStarted;
    return toRollupStatus(info.rollupStatus) ?? RollupStatus.NotStarted;
};

const getOnboardingSections = (
    statuses?: { [key: string]: StatusInfoDto },
    startDateStr?: string,
): OnboardingSection[] => {
    const lookup: Record<string, StatusInfoDto> = {};
    if (statuses) {
        for (const key in statuses) {
            if (Object.prototype.hasOwnProperty.call(statuses, key)) {
                lookup[key.toLowerCase()] = statuses[key];
            }
        }
    }

    const makeStep = (key: string, fallbackName: string, date?: string) => {
        const normalizedKey = key.toLowerCase();
        const info = lookup[normalizedKey];
        if (!info?.isRequired) return null;
        return {
            id: key,
            name: STATUS_DISPLAY_NAMES[normalizedKey] || fallbackName,
            status: resolveStatus(info),
            statusLabel: info?.status || undefined,
            date,
        };
    };

    const preHireSteps = [
        makeStep('backgroundCheck', 'Background check'),
        makeStep('immigration', 'Immigration'),
        makeStep('i9', 'I-9 documents'),
        makeStep('contract', 'Employment agreement'),
        makeStep('ecStatus', 'Employee profile created'),
        makeStep('relocation', 'Relocation'),
    ].filter((s): s is NonNullable<typeof s> => s !== null);

    const prepareSteps = [
        makeStep('startDate', 'Start date', startDateStr),
        makeStep('microsoftAccount', 'Microsoft account setup'),
    ].filter((s): s is NonNullable<typeof s> => s !== null);

    const handledKeys = new Set([
        'backgroundcheck', 'immigration', 'i9', 'contract',
        'ecstatus', 'relocation', 'startdate', 'microsoftaccount',
        'offeraccepted',
    ]);
    for (const key of Object.keys(lookup)) {
        if (!handledKeys.has(key)) {
            const step = makeStep(key, key);
            if (step) preHireSteps.push(step);
        }
    }

    return [
        { id: 'pre-hire', name: 'Pre-hire activities', steps: preHireSteps },
        { id: 'prepare-day1', name: 'Prepare for day 1', steps: prepareSteps },
    ];
};

const ROLE_TAB_CONFIG: Record<AppRole, { label: string; icon: typeof PersonRegular }> = {
    [AppRole.Manager]: { label: 'Hiring manager', icon: PersonRegular },
    [AppRole.OnboardingContact]: { label: 'Onboarding admin', icon: PeopleTeamRegular },
    [AppRole.Recruiter]: { label: 'Recruiter', icon: PersonChatRegular },
    [AppRole.WorkOnBehalf]: { label: 'Work on behalf', icon: GlobePersonRegular },
    [AppRole.Candidate]: { label: 'Candidate', icon: PersonRegular },
};

const ROLE_ORDER: AppRole[] = [
    AppRole.Manager,
    AppRole.OnboardingContact,
    AppRole.Recruiter,
    AppRole.WorkOnBehalf,
];

const SORT_FIELD_LABELS: Record<SortField, string> = {
    name: 'Name',
    jobTitle: 'Job title',
    startDate: 'Start date',
    requisitionNumber: 'Requisition number',
    hrCaseNumber: 'HR case number',
    location: 'Location',
    progress: 'Progress',
    hiringManagerEmail: 'Hiring manager email',
};

export const ManagerDashboard: React.FC = () => {
    const styles = useManagerDashboardStyles();
    const [managerSearchQuery, setManagerSearchQuery] = useState('');
    const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const sortAnnouncementRef = useRef<HTMLDivElement>(null);
    const paginationAnnouncementRef = useRef<HTMLDivElement>(null);
    const isInitialPageRef = useRef(true);
    const panelTriggerRef = useRef<HTMLElement | null>(null);
    const [selectedCandidate, setSelectedCandidate] = useState<CandidateDetails | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<AppRole>(AppRole.Manager);
    const [selectedManagerEmails, setSelectedManagerEmails] = useState<string[]>([]);
    const [knownWobManagers, setKnownWobManagers] = useState<WorkOnBehalfManager[]>([]);
    const [personSearchMatches, setPersonSearchMatches] = useState<WorkOnBehalfManager[]>([]);

    const { onNoAccess } = useNoAccess();

    const {
        roles,
        workOnBehalfHiringManagerEmails,
        loading: rolesLoading,
        error: rolesError,
    } = useUserRoles();

    const availableTabs = useMemo(() => {
        return ROLE_ORDER.filter(r => roles.indexOf(r) !== -1);
    }, [roles]);

    useEffect(() => {
        if (!rolesLoading && !rolesError && availableTabs.length === 0) {
            onNoAccess('You do not have access to view prehire candidates. Please contact your administrator.');
        }
    }, [rolesLoading, rolesError, availableTabs.length, onNoAccess]);

    const effectiveTab = useMemo((): AppRole | undefined => {
        if (availableTabs.indexOf(activeTab) !== -1) return activeTab;
        if (availableTabs.length > 0) return availableTabs[0];
        return undefined;
    }, [activeTab, availableTabs]);

    const { data: prehires, groupedData, loading: dataLoading, error: dataError } = usePrehiresData(
        effectiveTab,
        effectiveTab === AppRole.WorkOnBehalf ? selectedManagerEmails : undefined,
    );

    const { accounts, instance } = useMsal();

    const showSectionLoading = !rolesLoading
        && dataLoading
        && effectiveTab === AppRole.WorkOnBehalf
        && selectedManagerEmails.length > 0;

    const loading = rolesLoading || (dataLoading && effectiveTab !== AppRole.WorkOnBehalf);
    const error = rolesError || dataError;

    const userName = accounts?.[0]?.name;

    const candidates: Candidate[] = useMemo(() => {
        const source = prehires ?? [];
        if (source.length > 0) {
            return mapPrehiresToCandidates(source);
        }
        return [];
    }, [prehires]);

    const workOnBehalfManagers = useMemo<WorkOnBehalfManager[]>(() => {
        const entries = groupedData?.workOnBehalf ?? [];
        const managersByEmail = new Map<string, WorkOnBehalfManager>();

        for (const entry of entries) {
            const email = entry.hiringManagerEmail?.trim();
            if (!email) continue;

            const firstPrehire = entry.prehires?.[0];
            const displayName = firstPrehire?.hiringManager?.name?.trim()
                || [firstPrehire?.hiringManager?.firstName, firstPrehire?.hiringManager?.lastName].filter(Boolean).join(' ').trim()
                || email;

            const existing = managersByEmail.get(email);
            managersByEmail.set(email, {
                email,
                displayName,
                prehires: existing
                    ? [...existing.prehires, ...(entry.prehires ?? [])]
                    : [...(entry.prehires ?? [])],
            });
        }

        return Array.from(managersByEmail.values()).map(manager => ({
            email: manager.email,
            displayName: manager.displayName,
            prehires: Array.from(
                new Map(
                    manager.prehires.map(prehire => [prehire.crmCaseId ?? `${prehire.candidate?.email ?? ''}-${prehire.candidate?.fullName ?? ''}`, prehire]),
                ).values(),
            ),
        }));
    }, [groupedData?.workOnBehalf]);

    useEffect(() => {
        if (effectiveTab !== AppRole.WorkOnBehalf || workOnBehalfManagers.length === 0) {
            return;
        }

        setKnownWobManagers(previous => {
            const merged = new Map<string, WorkOnBehalfManager>();
            for (const manager of [...previous, ...workOnBehalfManagers]) {
                merged.set(manager.email.toLowerCase(), manager);
            }
            return Array.from(merged.values());
        });
    }, [effectiveTab, workOnBehalfManagers]);

    const allWobManagers = useMemo(
        () => (knownWobManagers.length > 0 ? knownWobManagers : workOnBehalfManagers),
        [knownWobManagers, workOnBehalfManagers],
    );

    const wobAliasFilters = useMemo(() => {
        return Array.from(
            new Set(
                workOnBehalfHiringManagerEmails
                    .map(email => email?.split('@')[0]?.trim().toLowerCase())
                    .filter((alias): alias is string => !!alias),
            ),
        );
    }, [workOnBehalfHiringManagerEmails]);

    const selectedManagers = useMemo(() => {
        return selectedManagerEmails.map(email => {
            const manager = allWobManagers.find(item => item.email.toLowerCase() === email.toLowerCase());
            return {
                email,
                displayName: manager?.displayName ?? email,
            };
        });
    }, [allWobManagers, selectedManagerEmails]);

    useEffect(() => {
        if (effectiveTab !== AppRole.WorkOnBehalf) {
            setPersonSearchMatches([]);
            return;
        }

        const query = managerSearchQuery.trim();
        if (query.length < 2 || wobAliasFilters.length === 0) {
            setPersonSearchMatches([]);
            return;
        }

        const selectedEmails = new Set(selectedManagerEmails.map(email => email.toLowerCase()));
        let isActive = true;

        const timeoutId = window.setTimeout(async () => {
            try {
                const results = await searchPersons(
                    {
                        input: query,
                        Aliases: wobAliasFilters,
                    },
                    instance,
                    accounts[0] ?? null,
                );

                if (!isActive) {
                    return;
                }

                const matchedManagers = results
                    .filter(result => !!result.userPrincipalName?.trim())
                    .filter(result => !selectedEmails.has(result.userPrincipalName!.trim().toLowerCase()))
                    .map(result => {
                        const email = result.userPrincipalName!.trim();
                        const knownManager = allWobManagers.find(manager => manager.email.toLowerCase() === email.toLowerCase());
                        const displayName = result.preferredFirstName
                            ? `${result.preferredFirstName}${result.preferredLastName ? ' ' + result.preferredLastName : ''}`
                            : knownManager?.displayName ?? email;
                        return {
                            email,
                            displayName,
                            prehires: knownManager?.prehires ?? [],
                        } as WorkOnBehalfManager;
                    });

                const uniqueManagers = Array.from(
                    new Map(matchedManagers.map(manager => [manager.email.toLowerCase(), manager])).values(),
                );

                setPersonSearchMatches(uniqueManagers);
            } catch (searchError) {
                console.error('Failed to search managers via PersonSearch:', searchError);
                if (isActive) {
                    setPersonSearchMatches([]);
                }
            }
        }, 250);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [
        effectiveTab,
        managerSearchQuery,
        wobAliasFilters,
        instance,
        accounts,
        selectedManagerEmails,
        allWobManagers,
    ]);

    const managerMatches = useMemo(() => {
        if (effectiveTab !== AppRole.WorkOnBehalf) {
            return allWobManagers;
        }

        if (!managerSearchQuery.trim()) {
            return [];
        }

        return personSearchMatches;
    }, [effectiveTab, managerSearchQuery, allWobManagers, personSearchMatches]);

    const showWobManagerResults = effectiveTab === AppRole.WorkOnBehalf && managerSearchQuery.trim().length > 0;

    const selectedManagersTooltipText = useMemo(
        () => selectedManagers.map(manager => manager.displayName).join(', '),
        [selectedManagers],
    );

    useEffect(() => {
        if (effectiveTab !== AppRole.WorkOnBehalf) {
            return;
        }

        if (selectedManagerEmails.length === 0) {
            return;
        }

        const managerEmails = new Set(allWobManagers.map(manager => manager.email.toLowerCase()));
        const remainingSelections = selectedManagerEmails.filter(email => managerEmails.has(email.toLowerCase()));

        if (remainingSelections.length !== selectedManagerEmails.length) {
            setSelectedManagerEmails(remainingSelections);
            setCurrentPage(1);
        }
    }, [effectiveTab, allWobManagers, selectedManagerEmails]);

    const tableCandidates = useMemo(() => {
        if (effectiveTab === AppRole.WorkOnBehalf) {
            return selectedManagerEmails.length > 0 ? candidates : [];
        }

        return candidates;
    }, [candidates, effectiveTab, selectedManagerEmails.length]);

    const statusSummary: StatusSummary[] = useMemo(() => {
        const summaryCandidates = effectiveTab === AppRole.WorkOnBehalf ? tableCandidates : candidates;
        const total = summaryCandidates.length;
        const notStarted = summaryCandidates.filter(c => c.completedSteps === 0 && !c.hasInProgressSteps).length;
        const complete = summaryCandidates.filter(c => c.completedSteps >= c.totalSteps).length;
        const inProgress = total - notStarted - complete;
        return [
            { label: 'Total', count: total, status: 'not-started' },
            { label: 'Not started', count: notStarted, status: 'not-started' },
            { label: 'In progress', count: inProgress, status: 'in-progress' },
            { label: 'Complete', count: complete, status: 'complete' },
        ];
    }, [candidates, effectiveTab, tableCandidates]);

    const filteredCandidates = useMemo(() => {
        let result = tableCandidates;
        if (candidateSearchQuery) {
            const query = candidateSearchQuery.toLowerCase();
            result = result.filter(c =>
                c.name.toLowerCase().includes(query) ||
                c.jobTitle.toLowerCase().includes(query) ||
                c.location.toLowerCase().includes(query) ||
                c.caseNumber.toLowerCase().includes(query) ||
                c.requisitionNumber.toLowerCase().includes(query)
            );
        }
        const sorted = [...result].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;
            switch (sortField) {
                case 'name': aVal = a.name; bVal = b.name; break;
                case 'jobTitle': aVal = a.jobTitle; bVal = b.jobTitle; break;
                // Candidates without a start date always sort last, regardless of direction.
                case 'startDate': {
                    const aTime = a.startDateValue;
                    const bTime = b.startDateValue;
                    if (aTime === undefined && bTime === undefined) return 0;
                    if (aTime === undefined) return 1;
                    if (bTime === undefined) return -1;
                    const cmp = aTime - bTime;
                    return sortDirection === 'asc' ? cmp : -cmp;
                }
                case 'requisitionNumber': aVal = a.requisitionNumber; bVal = b.requisitionNumber; break;
                case 'hrCaseNumber': aVal = a.hrCaseNumber; bVal = b.hrCaseNumber; break;
                case 'location': aVal = a.location; bVal = b.location; break;
                case 'hiringManagerEmail': aVal = a.hiringManagerEmail ?? ''; bVal = b.hiringManagerEmail ?? ''; break;
                case 'progress': aVal = a.totalSteps > 0 ? a.completedSteps / a.totalSteps : 0; bVal = b.totalSteps > 0 ? b.completedSteps / b.totalSteps : 0; break;
                default: aVal = a.name; bVal = b.name;
            }
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortDirection === 'asc' ? cmp : -cmp;
            }
            const diff = (aVal as number) - (bVal as number);
            return sortDirection === 'asc' ? diff : -diff;
        });
        return sorted;
    }, [candidateSearchQuery, sortDirection, sortField, tableCandidates]);

    const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE));

    useEffect(() => {
        if (isInitialPageRef.current) {
            isInitialPageRef.current = false;
            return;
        }
        if (paginationAnnouncementRef.current) {
            paginationAnnouncementRef.current.textContent =
                `Page ${currentPage} of ${totalPages} loaded, results table updated`;
        }
    }, [currentPage, totalPages]);

    const paginatedCandidates = filteredCandidates.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    const searchStatusMessage = useMemo(() => {
        if (!candidateSearchQuery) return '';
        if (filteredCandidates.length === 0) return 'No results found';
        return `${filteredCandidates.length} result${filteredCandidates.length === 1 ? '' : 's'} found`;
    }, [candidateSearchQuery, filteredCandidates.length]);

    const handleManagerSearchChange = (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        setManagerSearchQuery(data.value);
    };

    const handleCandidateSearchChange = (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        setCandidateSearchQuery(data.value);
        setCurrentPage(1);
    };

    const handleSelectManager = (managerEmail: string, managerDisplayName?: string) => {
        if (managerDisplayName) {
            setKnownWobManagers(previous => {
                if (previous.some(manager => manager.email.toLowerCase() === managerEmail.toLowerCase())) {
                    return previous;
                }
                return [...previous, { email: managerEmail, displayName: managerDisplayName, prehires: [] }];
            });
        }

        setSelectedManagerEmails(previous => {
            if (previous.some(email => email.toLowerCase() === managerEmail.toLowerCase())) {
                return previous;
            }
            return [...previous, managerEmail];
        });
        setManagerSearchQuery('');
        setCurrentPage(1);
    };

    const handleRemoveSelectedManager = (managerEmail: string) => {
        setSelectedManagerEmails(previous => previous.filter(email => email.toLowerCase() !== managerEmail.toLowerCase()));
        setCurrentPage(1);
    };

    const handleClearSelectedManagers = () => {
        setSelectedManagerEmails([]);
        setCurrentPage(1);
    };

    const getAriaSortValue = useCallback((field: SortField): 'ascending' | 'descending' | 'none' => {
        if (sortField !== field) return 'none';
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    }, [sortField, sortDirection]);

    const getSortButtonLabel = useCallback((field: SortField): string => {
        const label = SORT_FIELD_LABELS[field];
        if (sortField !== field) return `Sort by ${label}`;
        const nextDir = sortDirection === 'asc' ? 'descending' : 'ascending';
        return `Sort by ${label}, currently sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}, activate to sort ${nextDir}`;
    }, [sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        let newDirection: SortDirection;
        if (sortField === field) {
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            newDirection = 'asc';
        }
        setSortField(field);
        setSortDirection(newDirection);
        setCurrentPage(1);

        if (sortAnnouncementRef.current) {
            sortAnnouncementRef.current.textContent =
                `${SORT_FIELD_LABELS[field]} sorted ${newDirection === 'asc' ? 'ascending' : 'descending'}`;
        }
    };

    const handleViewDetails = (candidate: Candidate, event: React.MouseEvent<HTMLElement>) => {
        panelTriggerRef.current = event.currentTarget;
        const candidateDetails: CandidateDetails = {
            ...candidate,
            hiringManagerEmail: effectiveTab === AppRole.WorkOnBehalf ? candidate.hiringManagerEmail : undefined,
            onboardingSections: getOnboardingSections(candidate.statuses, candidate.startDate),
        };
        setSelectedCandidate(candidateDetails);
        setIsPanelOpen(true);
    };

    const handleClosePanel = () => {
        setIsPanelOpen(false);
        setSelectedCandidate(null);
        requestAnimationFrame(() => {
            panelTriggerRef.current?.focus();
            panelTriggerRef.current = null;
        });
    };

    if (loading) {
        return (
            <div className={styles.container} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spinner size="large" label="Loading candidates..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
                <p style={{ color: '#C4314B' }}>{error}</p>
                <Button appearance="primary" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {availableTabs.length > 1 && (
                <div className={styles.roleTabs} role="tablist" aria-label="View as">
                    <span className={styles.roleTabsLabel}>View as</span>
                    {availableTabs.map(role => {
                        const config = ROLE_TAB_CONFIG[role];
                        const Icon = config.icon;
                        const isActive = effectiveTab === role;
                        return (
                            <button
                                key={role}
                                role="tab"
                                aria-selected={isActive}
                                className={isActive ? styles.roleTabActive : styles.roleTab}
                                onClick={() => { setActiveTab(role); setCurrentPage(1); }}
                            >
                                <Icon fontSize={16} />
                                {config.label}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className={styles.heroWrapper}>
                <div className={styles.heroGradient}>
                    <div className={styles.heroContent}>
                        <h1 className={styles.heroTitle}>Welcome{userName ? ` ${userName}` : ''}</h1>
                        <p className={styles.heroSubtitle}>
                            {effectiveTab === AppRole.WorkOnBehalf
                                ? 'Search for a manager you can work on behalf of, then review their onboarding progress.'
                                : 'Track your new hires\' and transfers\' onboarding progress here'}
                        </p>
                    </div>
                </div>
                <div className={styles.heroDecoration} aria-hidden="true">
                    <img src={BannerEmoji} alt="" />
                </div>
                <div className={styles.heroBottom}>
                    <div className={styles.heroBottomTopRow}>
                        <div className={styles.summaryCards}>
                            {statusSummary.map(s => (
                                <Card
                                    key={s.label}
                                    className={styles.summaryCard}
                                    appearance="subtle"
                                    size="small"
                                >
                                    <span className={styles.summaryCardLabel}>{s.label}</span>
                                    <div className={styles.summaryCardValueRow}>
                                        <span className={styles.summaryCardCount}>{s.count}</span>
                                        <span className={styles.summaryCardUnit}>
                                            {s.count === 1 ? 'candidate' : 'candidates'}
                                        </span>
                                    </div>
                                </Card>
                            ))}
                        </div>
                        {effectiveTab === AppRole.WorkOnBehalf && (
                            <div className={styles.heroSearchArea}>
                                {selectedManagers.length > 0 && (
                                    <div className={styles.managerSelectionRow}>
                                        <Tooltip content={selectedManagersTooltipText} relationship="description">
                                            <div className={styles.selectedManagersList}>
                                                {selectedManagers.map(manager => (
                                                    <span key={manager.email} className={styles.selectedManagerChip} title={manager.displayName}>
                                                        <span className={styles.selectedManagerChipLabel}>{manager.displayName}</span>
                                                        <button
                                                            type="button"
                                                            className={styles.selectedManagerChipRemove}
                                                            aria-label={`Remove ${manager.displayName}`}
                                                            onClick={() => handleRemoveSelectedManager(manager.email)}
                                                        >
                                                            x
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </Tooltip>
                                    </div>
                                )}
                                <div className={styles.managerSearchRow}>
                                    <Input
                                        placeholder="Search for managers"
                                        contentBefore={<SearchRegular aria-hidden="true" />}
                                        value={managerSearchQuery}
                                        onChange={handleManagerSearchChange}
                                        className={styles.managerSearchInput}
                                        aria-label="Search for managers"
                                    />
                                    <button
                                        type="button"
                                        className={styles.clearSelectedManagersButton}
                                        onClick={handleClearSelectedManagers}
                                        disabled={selectedManagers.length === 0}
                                        aria-disabled={selectedManagers.length === 0}
                                    >
                                        Clear
                                    </button>
                                </div>
                                {showWobManagerResults && (
                                    <div className={styles.wobSearchDropdown} role="listbox" aria-label="Manager search results">
                                        {managerMatches.length === 0 ? (
                                            <div className={styles.wobSearchDropdownEmpty}>No managers match your search.</div>
                                        ) : managerMatches.map(manager => (
                                            <button
                                                key={manager.email}
                                                type="button"
                                                className={styles.wobSearchDropdownItem}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    handleSelectManager(manager.email, manager.displayName);
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {manager.displayName} ({manager.email})
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {effectiveTab === AppRole.WorkOnBehalf && selectedManagerEmails.length === 0 ? (
                <div
                    className={styles.emptyState}
                    style={{
                        minHeight: '360px',
                        padding: '56px 24px 72px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '16px',
                        border: '1px solid #E1DFDD',
                        justifyContent: 'center',
                    }}
                >
                    <img src={WobCheck} alt="" style={{ width: '160px', height: '160px', objectFit: 'contain', display: 'block' }} aria-hidden="true" />
                    <p className={styles.emptyStateText} style={{ maxWidth: '760px', textAlign: 'center', lineHeight: '22px' }}>
                        As a work-on-behalf who may support multiple managers (including nested teams), search for and select the managers you want to review with upcoming new hires or transfers.
                    </p>
                </div>
            ) : (
                <div className={styles.newHiresSection}>
                    {showSectionLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '260px' }}>
                            <Spinner size="large" label="Loading candidates..." />
                        </div>
                    ) : (
                        <>
                            <div className={styles.newHiresSectionHeader}>
                                <Input
                                    placeholder="Search by candidate name, requisition number, HR case number"
                                    contentBefore={<SearchRegular aria-hidden="true" />}
                                    value={candidateSearchQuery}
                                    onChange={handleCandidateSearchChange}
                                    className={styles.searchInput}
                                    aria-label="Search by candidate name, requisition number, HR case number"
                                />
                                <div
                                    aria-live="polite"
                                    aria-atomic="true"
                                    style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                                >
                                    {searchStatusMessage}
                                </div>
                            </div>

                            <div className={styles.tableContainer}>
                                <div
                                    ref={sortAnnouncementRef}
                                    aria-live="polite"
                                    aria-atomic="true"
                                    style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                                />
                                <table className={styles.table} aria-label="New hires onboarding status">
                                    <thead>
                                        <tr>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('name')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('name')} aria-label={getSortButtonLabel('name')}>
                                                    Name <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'name' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'name' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('jobTitle')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('jobTitle')} aria-label={getSortButtonLabel('jobTitle')}>
                                                    Job title <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'jobTitle' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'jobTitle' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('startDate')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('startDate')} aria-label={getSortButtonLabel('startDate')}>
                                                    Start date <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'startDate' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'startDate' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('requisitionNumber')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('requisitionNumber')} aria-label={getSortButtonLabel('requisitionNumber')}>
                                                    Requisition number <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'requisitionNumber' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'requisitionNumber' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('hrCaseNumber')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('hrCaseNumber')} aria-label={getSortButtonLabel('hrCaseNumber')}>
                                                    HR case number <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'hrCaseNumber' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'hrCaseNumber' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('location')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('location')} aria-label={getSortButtonLabel('location')}>
                                                    Location <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'location' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'location' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            {effectiveTab === AppRole.WorkOnBehalf && selectedManagerEmails.length > 0 && (
                                                <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('hiringManagerEmail')}>
                                                    <button type="button" className={styles.sortButton} onClick={() => handleSort('hiringManagerEmail')} aria-label={getSortButtonLabel('hiringManagerEmail')}>
                                                        Hiring manager email <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'hiringManagerEmail' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'hiringManagerEmail' ? 'black' : '#616161' }} />
                                                    </button>
                                                </th>
                                            )}
                                            <th scope="col" className={styles.tableHeader} aria-sort={getAriaSortValue('progress')}>
                                                <button type="button" className={styles.sortButton} onClick={() => handleSort('progress')} aria-label={getSortButtonLabel('progress')}>
                                                    Progress <ArrowSortDownRegular fontSize={12} aria-hidden="true" style={{ transform: sortField === 'progress' && sortDirection === 'asc' ? 'rotate(180deg)' : undefined, color: sortField === 'progress' ? 'black' : '#616161' }} />
                                                </button>
                                            </th>
                                            <th scope="col" className={styles.tableHeader}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedCandidates.length === 0 && (
                                            <tr>
                                                <td colSpan={effectiveTab === AppRole.WorkOnBehalf && selectedManagerEmails.length > 0 ? 9 : 8} className={styles.tableCell} style={{ textAlign: 'center', padding: '80px 16px' }}>
                                                    <div className={styles.emptyState}>
                                                        <img src={NoPrehireData} alt="" width="160" height="128" aria-hidden="true" />
                                                        <p className={styles.emptyStateText}>
                                                            {effectiveTab === AppRole.WorkOnBehalf && selectedManagerEmails.length > 0
                                                                ? 'No hires match this search.'
                                                                : 'You do not have any new hires starting in the near future. Check back soon!'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {paginatedCandidates.map(candidate => {
                                            const pct = candidate.totalSteps > 0
                                                ? (candidate.completedSteps / candidate.totalSteps) * 100
                                                : 0;
                                            const barType = getProgressBarClass(
                                                candidate.completedSteps,
                                                candidate.totalSteps,
                                                candidate.needsAttention,
                                            );
                                            const barClass = {
                                                complete: styles.progressBarComplete,
                                                inProgress: styles.progressBarInProgress,
                                                needsAttention: styles.progressBarNeedsAttention,
                                                notStarted: styles.progressBarNotStarted,
                                            }[barType];

                                            return (
                                                <tr key={candidate.id} className={styles.tableRow}>
                                                    <th scope="row" className={styles.tableCell}>
                                                        <span className={styles.candidateName} lang={detectLang(candidate.name)}>{candidate.name}</span>
                                                    </th>
                                                    <td className={styles.tableCell}>{candidate.jobTitle}</td>
                                                    <td className={styles.tableCell}>{candidate.startDate}</td>
                                                    <td className={styles.tableCell}>{candidate.requisitionNumber}</td>
                                                    <td className={styles.tableCell}>{candidate.hrCaseNumber}</td>
                                                    <td className={styles.tableCell}>{candidate.location}</td>
                                                    {effectiveTab === AppRole.WorkOnBehalf && selectedManagerEmails.length > 0 && (
                                                        <td className={styles.tableCell}>{candidate.hiringManagerEmail || '--'}</td>
                                                    )}
                                                    <td className={styles.tableCell}>
                                                        <div className={styles.progressCell}>
                                                            <div className={styles.progressTopRow}>
                                                                <span className={styles.progressText}>
                                                                    {candidate.completedSteps === candidate.totalSteps
                                                                        ? 'All completed'
                                                                        : `${candidate.completedSteps} of ${candidate.totalSteps} completed`}
                                                                </span>
                                                                {candidate.needsAttention && candidate.daysToStart !== undefined && (
                                                                    <Badge
                                                                        appearance="tint"
                                                                        color="danger"
                                                                        size="small"
                                                                        icon={<ErrorCircleRegular fontSize={12} />}
                                                                        style={{ marginLeft: '8px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                                                    >
                                                                        {candidate.daysToStart} days to start date
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className={styles.progressBarContainer}>
                                                                <div
                                                                    className={mergeClasses(styles.progressBarFill, barClass)}
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className={styles.tableCell}>
                                                        <Button
                                                            appearance="outline"
                                                            size="small"
                                                            onClick={(e) => handleViewDetails(candidate, e)}
                                                            aria-label={`View details for ${candidate.name}`}
                                                        >
                                                            View details
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div
                                ref={paginationAnnouncementRef}
                                aria-live="polite"
                                aria-atomic="true"
                                style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                            />
                            {totalPages > 1 && (
                                <nav className={styles.pagination} aria-label="Pagination">
                                    <button
                                        className={styles.pageNavButton}
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(p => p - 1)}
                                        aria-label="Previous page"
                                    >
                                        <ChevronLeftRegular fontSize={16} />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={page === currentPage ? styles.pageButtonActive : styles.pageButton}
                                            onClick={() => setCurrentPage(page)}
                                            aria-label={`Page ${page}`}
                                            aria-current={page === currentPage ? 'page' : undefined}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        className={styles.pageNavButton}
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(p => p + 1)}
                                        aria-label="Next page"
                                    >
                                        <ChevronRightRegular fontSize={16} />
                                    </button>
                                </nav>
                            )}
                        </>
                    )}
                </div>
            )}

            <CandidateDetailsPanel
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
                candidate={selectedCandidate}
            />
        </div>
    );
};
